// One context-aware registry for Brief navigation. Workspace root and unknown
// paths open Overview; a partner's entry opens its customer book. Direct run
// and mail-link URLs keep their existing contracts.

import { orgKindName, type OrgKind } from "../api/memberships";
import type { ContextChoice, SessionResponse } from "../api/session";
import { runIdFromLocation } from "../runId";
import { inboxViewOf, legacyKindOf, linkedWorkspaceOf } from "./inboxView";
import { isLegacyFindings } from "./postureTabs";

/**
 * The rail's groups, in the order it shows them (#795, decision 1). A group
 * with no entry this session shows is not drawn.
 */
export const GROUPS = ["Work", "Security", "Connections", "Workspace"] as const;
export type Group = (typeof GROUPS)[number];

/**
 * What a group is headed in this session's context: the fourth group is the
 * context's own administration, so an org's context reads its kind, "Partner" or
 * "Customer", where a workspace reads "Workspace" (#803, its heading revised by #1189
 * and #1205). Only the rail
 * says it; no screen repeats it above its heading (#799).
 */
export function groupHeading(
  group: Group,
  session: SessionResponse | null,
  orgKind: OrgKind | null = null,
): string {
  if (group === "Workspace" && session !== null && session.workspace_id === null && session.org_id !== null)
    return orgKindName(orgKind);
  return group;
}

/** A place the rail can send you. The id is the key `SCREENS` answers to. */
export interface Destination {
  readonly id: string;
  /**
   * What the rail, the command menu, the breadcrumb and the screen's own heading
   * call it: one string, so the four cannot disagree.
   */
  readonly label: string;
  /**
   * The one path that is this destination. A renamed destination keeps its path
   * (#803): mail and Slack links are built on these server-side
   * (`mail/links.py`), so a path is a contract and a label is not.
   */
  readonly path: string;
  /**
   * The rail group it sits in, or `"Account"`: the avatar menu, which holds what
   * is the person's own rather than the workspace's (#795, decision 1). The rail
   * draws `GROUPS` alone, so an `"Account"` entry is never in it.
   */
  readonly group: Group | "Account";
  /**
   * Who sees it in the rail (#417). Absent means everyone signed in. A screen is
   * still rendered at its path for anyone — it says why it is not theirs — so a
   * hidden entry is not a locked door, only an entry that would lead nowhere
   * useful for this session.
   */
  readonly when?: (session: SessionResponse) => boolean;
}

/**
 * Destinations appear only with their corresponding screen. Their order is the
 * rail's order within a group.
 */
export const DESTINATIONS = [
  {
    id: "overview",
    label: "Overview",
    path: "/",
    group: "Work",
    when: (session) => session.workspace_id !== null,
  },
  {
    id: "runs",
    label: "Investigations",
    path: "/runs",
    group: "Work",
    when: (session) => session.workspace_id !== null,
  },
  // The partner's customers (#417): a partner's context alone has any. It is
  // a partner's landing screen — `currentDestination` answers it for `/`
  // and `/runs` there — so it sits where a workspace's Overview does.
  {
    id: "customers",
    label: "Customers",
    path: "/customers",
    group: "Work",
    when: (session) => session.org_id !== null,
  },
  // What is waiting for this user to answer, across every context (#495): no
  // `when`, because it is the user's and not the context's. Approvals and Data
  // access are filters on it (#808); their old paths open those filters
  // (`inboxView.ts::legacyKindOf`), so a mailed link still lands.
  { id: "inbox", label: "Inbox", path: "/inbox", group: "Work" },
  // The platform admin's book (#417): every org, and the act that makes one.
  // The platform's counterpart of a partner's Customers.
  {
    id: "organisations",
    label: "Customers & partners",
    path: "/organisations",
    group: "Work",
    when: (session) => session.user.superadmin,
  },
  // The workspace's security posture and the findings behind it (#639, D14). Findings
  // is a tab of it (#814); its old path opens that tab (`postureTabs.ts`).
  {
    id: "posture",
    label: "Posture",
    path: "/posture",
    group: "Security",
    when: (session) => session.workspace_id !== null,
  },
  // The workspace's AWS accounts, and the wizard that connects one (#420).
  {
    id: "accounts",
    label: "AWS accounts",
    path: "/accounts",
    group: "Connections",
    when: (session) => session.workspace_id !== null,
  },
  // An AWS Organization's discovered accounts (#914): reached by its path and kept out of
  // the rail, whose groups and labels are decided (#795, #803), until the setup wizard
  // (#921) gives it an entry point.
  {
    id: "awsOrganization",
    label: "AWS Organization",
    path: "/aws-organization",
    group: "Connections",
    when: () => false,
  },
  // The workspace's alert sources (#422).
  {
    id: "sources",
    label: "Alert sources",
    path: "/sources",
    group: "Connections",
    when: (session) => session.workspace_id !== null,
  },
  // The workspace's Jira and Slack connections (#492), a page of their own (#805).
  {
    id: "integrations",
    label: "Integrations",
    path: "/integrations",
    group: "Connections",
    when: (session) => session.workspace_id !== null,
  },
  // The model the workspace's investigations use (#422), a page of its own (#805).
  {
    id: "provider",
    label: "Model provider",
    path: "/model-provider",
    group: "Connections",
    when: (session) => session.workspace_id !== null,
  },
  // Workspace settings, one section per path (#804).
  { id: "team", label: "Team & roles", path: "/settings/team", group: "Workspace" },
  { id: "policy", label: "Change policy", path: "/settings/policy", group: "Workspace" },
  // The workspace's answers to what organisations asked of it (#418).
  {
    id: "access",
    label: "Partner access",
    path: "/access",
    group: "Workspace",
    when: (session) => session.workspace_id !== null,
  },
  // The workspace's own usage this period, and its bill when it pays for itself
  // (#632, D6). A workspace an MSP pays for reads credits and no currency amount.
  {
    id: "spend",
    label: "Billing & usage",
    path: "/spend",
    group: "Workspace",
    when: (session) => session.workspace_id !== null,
  },
  // The workspace's own trail, and an organisation's own rows (D11, #637).
  {
    id: "audit",
    label: "Audit log",
    path: "/audit",
    group: "Workspace",
    when: (session) => session.workspace_id !== null || session.org_id !== null,
  },
  // The person's own passkeys and authenticator app (#804), in the avatar menu.
  { id: "account", label: "Your account", path: "/account", group: "Account" },
] as const satisfies readonly Destination[];

export type DestinationEntry = (typeof DESTINATIONS)[number];

/** The entries the rail shows this session. */
export function visibleDestinations(session: SessionResponse): readonly DestinationEntry[] {
  return DESTINATIONS.filter((entry) => {
    const { when } = entry as Destination;
    return when === undefined || when(session);
  });
}

export type DestinationId = (typeof DESTINATIONS)[number]["id"];

/** The registry's entry for `id`: what a screen's heading reads. */
export function destinationOf(id: DestinationId): Destination {
  return DESTINATIONS.find((entry) => entry.id === id)!;
}

/**
 * The doors a mail opens (#415): screens reached from a link rather than from
 * the rail, each carrying its token in the query string, and each rendered
 * **before the gate** — a person opening an invitation has no session, and a
 * verification link must work whether or not one is signed in. The link a mail
 * carries is built server-side from these same paths (`mail/links.py`), so a
 * door renamed here is a mail that opens nothing: the two are one contract.
 */
export const DOORS = ["invite", "verify", "reset", "forgot", "magic"] as const;
export type DoorId = (typeof DOORS)[number];

/** Where a location lands: a destination, a run, a door, or none of them. */
export type Route =
  | { kind: "destination"; id: DestinationId }
  | { kind: "run"; runId: string }
  | { kind: "door"; door: DoorId; token: string | null }
  | { kind: "unknown" };

/** Where Settings was one page (#804); its sections now have paths of their own. */
const SETTINGS_PATH = "/settings";

/** The workspace settings sections, in their sub-navigation's order (#804). */
export const SETTINGS_SECTIONS = ["team", "policy"] as const satisfies readonly DestinationId[];
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

/**
 * The section each anchor of the one-page Settings named. A bookmark or a mail
 * holding `/settings#team` lands on the section that holds it now.
 */
const SETTINGS_ANCHORS: ReadonlyMap<string, DestinationId> = new Map<string, DestinationId>([
  ["passkeys", "account"],
  ["authenticator", "account"],
  ["team", "team"],
  ["permissions", "team"],
  ["remediation", "policy"],
  ["provider", "provider"],
  ["integrations", "integrations"],
]);

/**
 * Where #804's General section was: it held the model provider and the
 * integrations until each became a Connections page of its own (#805).
 */
const GENERAL_PATH = "/settings/general";

/** The Connections pages an old anchor or a connecting link lands on, keeping its anchor. */
const CONNECTIONS_PAGES: ReadonlySet<DestinationId> = new Set<DestinationId>(["provider", "integrations"]);

/**
 * Where a location on the old one-page Settings, or on #804's General, belongs
 * now, or null for any other location. Only a browser can answer it, because a
 * hash never reaches the server. Bare `/settings` is Your account (#804) and bare
 * General is the model provider, its first panel; a link into a connecting flow
 * (`integrationsStep.ts::connectHref`) is Integrations, where the flow lives. The
 * query is kept, and so is an anchor the page it lands on still has.
 */
export function settingsRedirectOf(
  pathname: string,
  search: string,
  hash: string,
): { pathname: string; search: string; hash: string } | null {
  const path = normalise(pathname);
  if (path !== SETTINGS_PATH && path !== GENERAL_PATH) return null;
  const anchor = hash.replace(/^#/, "");
  const id: DestinationId =
    SETTINGS_ANCHORS.get(anchor) ??
    (new URLSearchParams(search).has("connect")
      ? "integrations"
      : path === GENERAL_PATH
        ? "provider"
        : "account");
  return {
    pathname: destinationOf(id).path,
    search,
    hash: CONNECTIONS_PAGES.has(id) && anchor !== "" ? `#${anchor}` : "",
  };
}

/** The destination that catches a path no route claims. */
export const FALLBACK: DestinationId = "overview";

function normalise(pathname: string): string {
  if (pathname === "" || pathname === "/") return "/";
  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

/**
 * Which route `pathname` names.
 *
 * The run page is matched first, by the same `runIdFromLocation` the page has
 * always used: one reader of that shape, so the link a row renders and the
 * route that answers it cannot drift apart.
 */
export function routeFor(pathname: string, search = ""): Route {
  const runId = runIdFromLocation(pathname);
  if (runId !== null) return { kind: "run", runId };
  const path = normalise(pathname);
  const door = DOORS.find((candidate) => `/${candidate}` === path);
  if (door !== undefined) {
    const token = new URLSearchParams(search).get("token");
    return { kind: "door", door, token: token === "" ? null : token };
  }
  const destination = DESTINATIONS.find(
    (candidate) => normalise(candidate.path) === path,
  );
  if (destination !== undefined)
    return { kind: "destination", id: destination.id };
  // The page Settings was (#804): what is left at its path is the person's own.
  if (path === SETTINGS_PATH) return { kind: "destination", id: "account" };
  // The screen Posture took as a tab (#814): its path opens Posture.
  if (isLegacyFindings(path)) return { kind: "destination", id: "posture" };
  // A screen the Inbox took as a filter (#808): its path opens the Inbox.
  if (legacyKindOf(path) !== null) return { kind: "destination", id: "inbox" };
  return { kind: "unknown" };
}

/**
 * The destination a location shows in the rail — the fallback for anything the
 * table does not claim, so the rail always marks exactly one entry current.
 *
 * A run page is under no destination: it is reached from a row, and marking
 * "Runs" current there would tell a person the rail took them somewhere it did
 * not. `null` says "no entry is current", which is what the rail renders.
 */
export function currentDestination(
  route: Route,
  session?: SessionResponse,
): DestinationId | null {
  if (
    session?.workspace_id === null &&
    session.org_id !== null &&
    (route.kind === "unknown" ||
      (route.kind === "destination" &&
        (route.id === "runs" || route.id === "overview")))
  )
    return "customers";
  if (route.kind === "destination") return route.id;
  if (route.kind === "unknown") return FALLBACK;
  return null;
}

/** The path a door lives at, for the links a screen renders to another door. */
export function doorPath(door: DoorId): string {
  return `/${door}`;
}

/**
 * A link into one change, in the context that answers it (#266): what a Slack card
 * carries, built server-side by `mail/links.py::Links.change` — one URL scheme for a
 * mail and a post. `/changes?change=<id>&workspace=<id>`, or `&org=<id>` when an
 * organisation's grant holds the change.
 */
export interface ChangeLink {
  readonly changeId: string;
  readonly context: ContextChoice;
}

/** The change link `pathname` and `search` name, or null for any other location. */
export function changeLinkOf(pathname: string, search: string): ChangeLink | null {
  if (legacyKindOf(pathname) !== "change_approval") return null;
  const query = new URLSearchParams(search);
  const changeId = query.get("change");
  const workspace = linkedWorkspaceOf(query);
  const org = query.get("org");
  if (!changeId || Boolean(workspace) === Boolean(org)) return null;
  return { changeId, context: workspace ? { workspace_id: workspace } : { org_id: org! } };
}

/**
 * The context a link into the console names (#266), or null: a change link's
 * (`changeLinkOf`), an Inbox item's (`inboxViewOf`: the drawer answers in the item's
 * own context, #808), or an investigation's page carrying its workspace,
 * `/runs/<id>?workspace=<id>` — what a Jira ticket links, built by `Links.run`.
 */
export function linkedContextOf(pathname: string, search: string): ContextChoice | null {
  const change = changeLinkOf(pathname, search);
  if (change !== null) return change.context;
  const inbox = inboxViewOf(pathname, search);
  if (inbox !== null) return inbox.open?.context ?? null;
  if (routeFor(pathname, search).kind !== "run") return null;
  const workspace = linkedWorkspaceOf(new URLSearchParams(search));
  return workspace ? { workspace_id: workspace } : null;
}

/**
 * Whether the session already reads under the link's context. An organisation's
 * context is the org with no workspace selected: an org answers a change only there
 * (`store/changes.py::may_answer`).
 */
export function inLinkContext(session: SessionResponse, choice: ContextChoice): boolean {
  if ("workspace_id" in choice) return session.workspace_id === choice.workspace_id;
  return session.workspace_id === null && session.org_id === choice.org_id;
}
